<template>
	<div v-infinite-scroll="loadMore">
		<slot :data="data" :loading="loading" :loadingPage="loadingPage" :totalResults="totalResults" :save="save" :saving="saving" />
	</div>
</template>

<script setup>
import Mango from './mango'
import useOffline from './offline'
import Swal from './swal' // lazy sweetalert2 (HAP-4635)
import 'sweetalert2/dist/sweetalert2.min.css';
import {siteName} from '../../../config/config/settings.json'

import { ref, watch, computed, nextTick, onUnmounted } from 'vue'
// import { useRouter, useRoute, onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'
import { useRoute, onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'

const props = defineProps({
    collection: {type: String},
    algoliaSearch: {type: String},
    algoliaFilters: {type: String},
    id: {type: String},
    query: {type: Object},
    graphql: {type: String},
    debounce: {type: Number, default: 500},
    infinite: {type: Boolean, default: false},
    suspend: {type: Boolean, default: false},
    disabled: {type: Boolean, default: false},
    main: {type: Boolean, default: false},
    globalSearch: {type: Boolean, default: false},
    subscribe: {type: Boolean, default: false},
})

/*
    If I comment this out, some things are fixed and others are broken...
*/
onBeforeRouteLeave((to, from) => {
    // if same component, do nothing (fixes issue with this not firing when changing topic in resources)
    if (to.name === from.name) return
    else active.value = false
})

// `update:totalResults` fires on the Algolia path only, which is why an
// undeclared emit went unnoticed: browsing never triggered it, and every warning
// arrived mid-search where nobody was reading the console.
const emit = defineEmits(['update:data','update:loading','update:totalResults','update:error'])

const route = useRoute()

let active = ref(true)
let autoPage = ref(null)
let loadingPage = ref(false)
let noneRemain = ref(false)
let saving = ref(false)
let inferedCollection = ref(props.collection || props.query?.collection || route.params?.collection || route.path.split('/')?.[1] || null)

let loading = ref(false)
let data = ref(null)
let error = ref(null)
let totalResults = ref(null)
let oldQuery = JSON.stringify(props.query || {})

let inferedId = computed(() => props.id !== undefined ? props.id : props.query?.id || route.params?.id || null )
let searchingAlgolia = computed(() => !!props.algoliaSearch || !!props.algoliaFilters )

let debounceInit = computed(() => {
    let timer;
    return (...args) => {
        if (active.value === false || props.disabled) return
        loading.value = true
        clearTimeout(timer);
        timer = setTimeout(() => { init.apply(this, args); }, props.debounce);
    };
})

watch(() => props.query, (n, o) => {
    let newQuery = JSON.stringify(n)
    if (oldQuery != newQuery) defer(newQuery, oldQuery, 'query')
}, { deep: true })

watch(inferedId, (n, o) => defer(null, null, 'id'))
watch(() => props.collection, (n, o) => defer(null, null, 'collection'))
watch(() => props.algoliaSearch, (n, o) => searchingAlgolia.value ? debounceInit.value() : init())
watch(() => props.algoliaFilters, (n, o) => searchingAlgolia.value ? debounceInit.value() : init())
watch(loading, (n, o) => emit('update:loading', loading.value))
// Surface fetch failures so single-resource pages can show a real error state
// instead of a blank page when the API 502s or a session expires (HAP-4243).
watch(error, () => emit('update:error', error.value))
watch(data, () => {
    if (data?.value?.length && props.query?.limit && data?.value?.length < props.query?.limit) noneRemain.value = true
    if (data?.value) emit('update:data', data.value)
})


function defer(n,o,origin) {
    // console.log('n,o,origin', n,o,origin)
    if (n == o && origin == 'query') return console.log(`they're the same...`)
    if (origin == 'query') oldQuery = n
    nextTick(() => init())
}

async function loadMore() {

    // console.log('loadMore')

    if (!props.infinite || !data?.value?.length || loadingPage.value || noneRemain.value || props.disabled) return
    loadingPage.value = true
    let query = props.query ? JSON.parse(JSON.stringify(props.query)) : {}
    if (query.search && !query.search?.wordSearch) delete query.search.wordSearch
    autoPage.value ++
    query.page = autoPage.value
    let nextPage
    try {
        if (searchingAlgolia.value) {
            nextPage = (await Mango[inferedCollection.value].search(props.algoliaSearch, query, props.algoliaFilters))?.hits
        } else if (inferedCollection.value?.includes('/')) {
            nextPage = await Mango.relationRequest({...query, path: inferedCollection.value})
        } else {
            nextPage = await Mango[inferedCollection.value](query)
        }
    } catch (e) {
        // Expected for anon/unauthenticated users on protected collections (CFL-FRONT-T);
        // treat as end-of-list instead of surfacing an unhandled rejection.
        noneRemain.value = true
        loadingPage.value = false
        return
    }
    if (nextPage?.length) data.value = (data.value ?? []).concat(nextPage)
    if (!nextPage?.length || (query.limit && nextPage.length < query.limit)) noneRemain.value = true
    loadingPage.value = false

}

async function init() {

    console.log('init', 'active', active.value)

    if (active.value === false || props.disabled) return

    // If the main entry is provided in ssr
    if (props.main && window.mainEntry && window.mainEntry?.id == inferedId.value) {
        data.value = window.mainEntry
        window.mainEntry = null
        emit('update:data', data.value)
        return
    }


    loading.value = true
    data.value = null
    error.value = null
    noneRemain.value = false
    autoPage.value = props.query?.page || 0

    // Using the computed here won't work because it hasn't been computed yet
    let searchingAlgolia = !!props.algoliaSearch || !!props.algoliaFilters

    let collection = inferedCollection.value
    let validatedCollection = Mango.collections.find(c => c.name == collection)
    let id = props.id || props.query?.id || route.params?.id || null
    if (props.id !== undefined) id = props.id // So you can pass null to id and get a list

    if (props.graphql){
        data.value = await Mango.graphql(props.graphql)
        emit('update:data', data.value)
        loading.value = false

        if (props.subscribe) {
            let subscribeCollection = `${validatedCollection.singular}Change`
            console.log('subscribeCollection', subscribeCollection)
            subscribe(subscribeCollection, id)
        }

        return
    }

    let query = props.query ? JSON.parse(JSON.stringify(props.query)) : {}
    if (query.search && !query.search?.wordSearch) delete query.search.wordSearch // So empty search still returns something

    if (inferedCollection.value?.includes('/')) {
        data.value = await Mango.relationRequest({...query, path: inferedCollection.value})
        emit('update:data', data.value)
        loading.value = false
        return
    }

    // Global Algolia Search
    if (props.globalSearch) {
        data.value = await Mango.search(props.algoliaSearch, query, props.algoliaFilters)
        if (data.value?.nbHits != null) {
            totalResults.value = data.value.nbHits
            emit('update:totalResults', data.value.nbHits)
        }
        emit('update:data', data.value)
        loading.value = false
        return
    }

    if (!validatedCollection) return console.error(`🥭 ${collection} is not a valid collection.`)
    if (id && !searchingAlgolia) collection = validatedCollection.singular
    // if (id) query = id

    // console.log('collection, id:', collection, id)
    // console.log('collection', collection)

    if (searchingAlgolia) {
        // console.log('collection', collection)
        try {
            let algoliaResponse = await Mango[collection].search(props.algoliaSearch, query, props.algoliaFilters)
            data.value = algoliaResponse.hits
            totalResults.value = algoliaResponse.nbHits
            emit('update:totalResults', algoliaResponse.nbHits)
            emit('update:data', data.value)
        } catch (e) {
            // Algolia client can reject on network/config errors (CFL-FRONT-1M);
            // degrade to an empty result set instead of an unhandled rejection.
            console.error('🥭 Algolia search failed', e)
            data.value = []
            totalResults.value = 0
            emit('update:totalResults', 0)
            emit('update:data', data.value)
        }
        loading.value = false
    } else {
        try {
            if (id) data.value = await Mango[collection](id, query)
            else data.value = await Mango[collection](query)
        } catch (e) {
            // Offline fallback (HAP-1422): if the network request fails and this
            // is a single resource the visitor saved for offline use, hydrate the
            // detail page from the local library instead of showing a blank page.
            if (id && inferedCollection.value === 'resources') {
                let saved = await useOffline().getResource(id)
                if (saved) data.value = saved
            }
            if (data.value == null) {
                // Record the failure (502, expired token, network) so the page can
                // render an error/retry state rather than blanking (HAP-4243).
                error.value = e
                loading.value = false
                return
            }
        }

        emit('update:data', data.value)
        loading.value = false

        if (id && props.main) document.title = data.value?.title || siteName || ''
        // else document.title = siteName

        return
    }

}

let webSocket
const GQL = {
    CONNECTION_INIT: 'connection_init',
    CONNECTION_ACK: 'connection_ack',
    CONNECTION_ERROR: 'connection_error',
    CONNECTION_KEEP_ALIVE: 'ka',
    START: 'start',
    STOP: 'stop',
    CONNECTION_TERMINATE: 'connection_terminate',
    DATA: 'data',
    ERROR: 'error',
    COMPLETE: 'complete'
}
function subscribe(collection, id) {

    if (!props.graphql) return console.error(`🥭 subscriptions require a graphql query to be passed.`)

    let gqlQueryFields = props.graphql.split('{')
    gqlQueryFields.splice(0,1)
    gqlQueryFields = gqlQueryFields.join('{')
    gqlQueryFields = gqlQueryFields.split('}')
    gqlQueryFields.pop()
    gqlQueryFields = gqlQueryFields.join('}')

    webSocket = new WebSocket(Mango.ws, 'graphql-ws')

    webSocket.onopen = event => {

        webSocket.send(JSON.stringify({
            type: GQL.CONNECTION_INIT,
            payload: {}
        }))

        webSocket.send(JSON.stringify({
            type: GQL.START,
            id: Date.now(),
            payload: {
                query: `
                subscription {
                    ${collection}(id: "${id}") {
                        ${gqlQueryFields}
                    }
                }`,
                variables: {},
                operationName: null
            }
        }))
    }

    webSocket.onmessage = event => {
        const response = JSON.parse(event.data)
        switch (response.type) {
            case GQL.CONNECTION_ACK: {
                console.log('success')
                break
            }
            case GQL.CONNECTION_ERROR: {
                console.error(response.payload)
                break
            }
            case GQL.CONNECTION_KEEP_ALIVE: {
                break
            }
            case GQL.DATA: {
                console.log(response.id, response.payload.errors, response.payload.data)
                if (response.payload.data) {
                    data.value = response.payload.data[collection]
                    emit('update:data', data.value)
                }
                break
            }
            case GQL.COMPLETE: {
                console.log('completed', response.id)
                break
            }
        }
    }
}

onUnmounted(() => {
    if (webSocket?.send) {
        webSocket.send(JSON.stringify({
            type: GQL.CONNECTION_TERMINATE
        }))
    }
})

// init()
// await init()

if (props.suspend) {
    await init()
} else {
    init()
}
</script>

<script>
export default {
    directives: {
        infiniteScroll: {
            mounted(element, binding) {
                document.addEventListener("scroll", () => binding.dir.methods.handleScroll(element, binding.value))
            },
            unmounted(element, binding) {
                document.removeEventListener("scroll", () => binding.dir.methods.handleScroll(element, binding.value))
            },
            methods: {
                handleScroll(element, method) {
                    // console.log('triggered')
                    if (element.getBoundingClientRect().bottom - 100 < window.innerHeight) method()
                }
            }
        }
    },
	methods: {
        async save(data) {
            data = data || this.data
            if (Array.isArray(data)) {
                console.log('You can only call save after querying for an individual document.')
                return
            } else {
                if (this.saving) return
                this.saving = true
                let response = await Mango[this.inferedCollection].save(data)
                this.saving = false
                // if (response?.id) return Swal.fire({ title: 'Success!', icon: 'success', confirmButtonText: 'Awesome!' })
                // return Swal.fire({ title: 'Error 😬', icon: 'error' })
            }
        }
	},
    activated() { this.active = true },
    // beforeRouteLeave() {
    //     this.active = false
    // },
    // beforeRouteUpdate() {
    //     this.active = false
    // },
    deactivated() { this.active = false },
}
</script>
